/**
 * Shared SDK `fields` projections for the social page.
 *
 * All four boards, the floating rank bar, and the podium consume the ONE
 * unified `useGetLeaderboard` / `useGetLeaderboardRank` row (`kind`-discriminated).
 * Each surface scopes `fields=...` to its board's metrics + `user` (FQ-097). The
 * SDK keys its dedup cache on `[path, params, fields]`, so a prefetch and the
 * hook that reads it back MUST pass byte-identical arrays — otherwise the
 * prefetch warms a different slot than the hook reads and the request fires
 * twice. Hoisted here so prefetch + hook can never drift.
 *
 * `user` selects the full identity projection declared on the hook (incl.
 * `heroUrl`/`heroNsfw` for the podium); the irrelevant board fields are returned
 * `null` by the backend.
 */

import type { SocialTab } from '@/components/social/types';

export const LB_TRADERS_FIELDS = [
  'rowId',
  'user',
  'totalPnl',
  'realizedPnl',
  'totalVolume',
  'totalTrades',
  'marketsTraded',
  'pnlRank',
  'volumeRank',
  'tradesRank',
  'marketsRank',
] as const;

export const LB_BOOSTERS_FIELDS = [
  'rowId',
  'user',
  'totalAmountBoosted',
  'numBoosts',
  'marketsBoosted',
  'amountRank',
  'boostsRank',
  'marketsBoostedRank',
] as const;

export const LB_BACKERS_FIELDS = [
  'rowId',
  'user',
  'totalBackedNetD6',
  'numBacks',
  'topMarketPubkey',
  'topMarketTitle',
  'topMarketVolume',
  'topMarketRank',
  'amountRank',
  'backsRank',
] as const;

export const LB_CREATORS_FIELDS = [
  'rowId',
  'user',
  'topMarketPubkey',
  'topMarketTitle',
  'topMarketVolume',
  'totalMarketsCreated',
  'creatorTotalVolume',
  'topMarketRank',
  'marketsCreatedRank',
  'totalVolumeRank',
] as const;

/**
 * Per-tab field projection, keyed by SocialClient's tab id. The podium and the
 * list tab MUST pass the SAME fields for a tab so they resolve to ONE shared
 * `useGetLeaderboard` cache entry (the queryKey is `[kind, {sortBy, sortOrder,
 * window, anchorUserId}, fields]` — `limit` is NOT in it, so `fields` is the
 * only thing that was splitting them). One shared entry ⇒ one fetch + one
 * realtime stream ⇒ the podium's top-3 is, by construction, always the list's
 * first 3 rows, and a NATS re-rank moves both together (a separate 3-row podium
 * query could never see an entrant climbing in from outside its window).
 */
export const LB_FIELDS_BY_TAB: Record<SocialTab, readonly string[]> = {
  leaderboard: LB_TRADERS_FIELDS,
  boosters: LB_BOOSTERS_FIELDS,
  creators: LB_CREATORS_FIELDS,
  backers: LB_BACKERS_FIELDS,
};
